/**
 * Rate limit configuration for all endpoints
 *
 * Multi-tier rate limiting:
 * - Layer 1: Cloudflare native (IP-based, fast, at edge)
 * - Layer 2: Redis (user-based, granular, for authenticated endpoints)
 */

import { DATA_PROXY_PREFIX } from './contracts/rekor-attestation';
import { BASE_DESTRUCTION_CONFIRM_PATH, BASE_DESTRUCTION_PREFIX } from './contracts/base-destruction';

/**
 * Rate limit tiers using Cloudflare bindings
 */
export const RATE_LIMIT_TIERS = {
  /** Very restrictive - OAuth, auth endpoints (5/min) */
  STRICT: { limit: 5, period: 60, bindingName: 'RATE_LIMITER_STRICT' },
  /** Moderate - billing, token exchange (30/min) */
  MODERATE: { limit: 30, period: 60, bindingName: 'RATE_LIMITER_MODERATE' },
  /** Standard - general API endpoints (100/min) */
  STANDARD: { limit: 100, period: 60, bindingName: 'RATE_LIMITER_STANDARD' },
  /** High throughput - message webhooks (300/min) */
  HIGH: { limit: 300, period: 60, bindingName: 'RATE_LIMITER_HIGH' },
} as const;

export type RateLimitTier = keyof typeof RATE_LIMIT_TIERS;

/**
 * Configuration for a specific endpoint's rate limiting
 */
export interface EndpointRateLimitConfig {
  /** IP-based tier using Cloudflare binding */
  tier: RateLimitTier;
  /** User-based limit (requests per minute) - only for authenticated requests */
  userLimit?: number;
  /** Window in seconds for user limit (default: 60) */
  userWindow?: number;
  /** If true, return 503 when Redis is unavailable (default: false = fail-open) */
  failClosed?: boolean;
  /** If true, skip IP rate limit for authenticated users (default: false) */
  skipAuthenticatedUsers?: boolean;
  /**
   * Canonical identity for the per-user budget. Use when path parameters are
   * sensitive or every matched path must consume one shared budget.
   */
  userLimitKey?: string;
}

/**
 * Endpoint configurations
 * Keys are path patterns that support wildcards (*)
 */
const ENDPOINT_CONFIGS: Record<string, EndpointRateLimitConfig> = {
  // OAuth callbacks - strict, fail-closed (prevent brute force)
  '/oauth/callback/*': { tier: 'STRICT', failClosed: true },

  // Auth endpoints - strict, fail-closed
  '/auth/phone': { tier: 'STRICT', failClosed: true },
  '/auth/mcp/exchange': { tier: 'MODERATE', failClosed: true },
  // Native magic-code login (pre-auth) — STRICT per-IP bounds a single host; WorkOS owns
  // the per-email lockout / TTL / single-use guarantees.
  '/api/auth/magic-code/*': { tier: 'STRICT', failClosed: true },
  // Native password login (pre-auth) — same posture as magic-code above. STRICT per-IP bounds a
  // single host but FAILS OPEN on a binding error (like every IP limit); the `failClosed` flag
  // here is inert on a pre-auth route (it's only enforced on the per-user branch, which needs an
  // authenticated caller). The fail-CLOSED brute-force guarantee for the static credential comes
  // from the route's per-email caps (burst + cumulative hourly), not this flag.
  '/api/auth/password/*': { tier: 'STRICT', failClosed: true },

  // Billing - moderate, fail-closed with user limits
  '/api/billing/*': { tier: 'MODERATE', userLimit: 100, failClosed: true, skipAuthenticatedUsers: true },
  '/webhooks/stripe': { tier: 'MODERATE', failClosed: true },
  '/webhooks/workos': { tier: 'MODERATE', failClosed: true },

  // API endpoints - standard with user limits
  '/api/setup/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/conversations/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/users/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/analytics/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/evals/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/files/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/outbound/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/live-updates/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/hub-users/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/conversation-state/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },

  // Channel webhooks - high throughput (external systems, never authenticated)
  '/webhooks/whatsapp/*': { tier: 'HIGH' },
  '/webhooks/instagram/*': { tier: 'HIGH' },

  // Reports (CLI → platform_report triage queue). One per-user budget covers intake
  // (POST), the content edit (PATCH /:id), the reporter read-back (GET, GET /:id), and the
  // verification loop (POST /:id/accept, /:id/contest). The limiter is path-based, so a
  // method-level split (loose reads, strict writes) isn't expressible — GET /:id and PATCH
  // /:id share a path. So the budget is sized for the read-heavy poll loop (a reporter agent
  // polling `report get`/`list` while it waits for the fix), at 60/hour. Intake-flooding stays
  // bounded regardless: identical reports collapse on `fingerprint` (ON CONFLICT DO NOTHING),
  // so a runaway create loop merges rather than floods. Still STRICT tier + failClosed so a
  // genuinely runaway distinct-report loop surfaces as 429. The `/*` is load-bearing — an
  // exact `/api/reports` key would leave the `/:id` paths unmatched (→ unthrottled).
  // `userLimitKey` is equally load-bearing: without it, middleware falls back to each raw
  // pathname and report IDs/actions receive independent counters instead of this shared budget.
  '/api/reports/*': {
    tier: 'STRICT',
    userLimit: 60,
    userWindow: 3600,
    failClosed: true,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/reports/*',
  },

  // Admin report mutations (transition/group/ungroup/edit) — platform-admin
  // gated at the route, rate-limited here as defense-in-depth. Generous STANDARD
  // cap so legitimate triage bursts aren't throttled (grouping is one request
  // even for many members); deliberately NOT failClosed so a Redis blip never
  // blocks an admin's triage. The bare GET reads share this budget harmlessly.
  '/api/admin/reports/*': { tier: 'STANDARD', userLimit: 150 },

  // GitHub webhook (issues.closed → platform_report addressed). MODERATE +
  // fail-closed; GitHub retries on transient failures so a brief Redis blip
  // doesn't lose state.
  '/webhooks/github': { tier: 'MODERATE', failClosed: true },

  // Account deletion — strict on the destructive POST + public undo. The
  // /api/users/* default tier above ALSO matches these, but `getEndpointConfig`
  // returns the longest-prefix match, so the more specific entries here win.
  // `skipAuthenticatedUsers` deliberately omitted: every caller is authenticated,
  // so including it would make `userLimit` a no-op — the per-user cap IS the gate.
  '/api/users/me/deletion': { tier: 'STRICT', userLimit: 3, userWindow: 3600, failClosed: true },
  '/api/users/me/deletion/preflight': { tier: 'STANDARD', userLimit: 30, failClosed: true },
  '/api/users/deletion/undo': { tier: 'STRICT', failClosed: true },

  // Production-base destruction.
  //
  // Keyed off the CONTRACT constants, not literals: `BASE_DESTRUCTION_CONFIRM_PATH`'s own
  // docstring promises that the mount, the `AUTH_BYPASS_PATHS` entry, the rate-limit key and
  // the URL the email embeds move together, and a literal here is exactly how that promise
  // goes quietly false — leaving an unauthenticated HMAC endpoint on the default tier.
  //
  // The emailed GET is PUBLIC, so it gets the IP-tier bound the pre-auth routes get.
  // `failClosed` is deliberately NOT set: it is only consulted on the per-USER branch, which
  // needs an authenticated caller, so on a pre-auth route it is inert — the same thing
  // `/api/auth/magic-code/send` above says about itself. The IP limiter fails OPEN on a
  // limiter error by design (`packages/runtime/src/rate-limiter.ts`), and claiming otherwise
  // would tell the next auditor a protection exists that does not.
  [BASE_DESTRUCTION_CONFIRM_PATH]: { tier: 'STRICT' },
  // The console's read, which every production base's settings pane issues on mount. Without
  // its own entry it inherits the mount-wide budget below, and an admin clicking through
  // thirty bases in an hour would be 429'd by a limit sized for irreversible writes.
  [`${BASE_DESTRUCTION_PREFIX}/destructions`]: { tier: 'STANDARD', userLimit: 150 },
  // The mutating routes: initiate, the authenticated confirm, and cancel. Per user rather than
  // per IP — all three are authenticated and org-admin gated — and hourly, since a single human
  // touching thirty production-deletion operations in an hour is already far past normal.
  // Longest-prefix wins, so the two entries above are not widened by this one.
  [`${BASE_DESTRUCTION_PREFIX}/*`]: { tier: 'STANDARD', userLimit: 30, userWindow: 3600, failClosed: true },

  // Email-invite preview — public, fail-closed. MODERATE (30/min IP) leaves
  // headroom for the signup-page server-fetch + reloads + prefetches; STRICT
  // would throttle legitimate users opening the link in multiple tabs.
  '/api/invites/preview': { tier: 'MODERATE', failClosed: true },

  // Admin DO debug reads — the ONE deliberate deviation from the Data Explorer
  // catch-all below, and the only reason a per-route key still exists here.
  // Platform-admin is enforced at the route; this is the independent
  // defense-in-depth signal so a runaway investigation script surfaces as 429.
  //
  // STRICT is kept ON PURPOSE even though the catch-all moved off it, because
  // `tier` is the only layer that bounds enumeration on this surface: the
  // per-user cap keys on the RAW pathname (see the CAVEAT below), so sweeping
  // `/debug/do/hub/<id>/tables` across thousands of ids never trips it. At 5/min
  // vs STANDARD's 100/min, walking 10k ids takes ~33h instead of ~100min — a
  // detection window worth keeping on the routes that return RAW DO ROWS.
  //
  // The cost, stated rather than elided: STRICT is one bucket shared with
  // `/oauth/callback/*` and `/api/auth/magic-code/*`, so a debug session from a
  // NAT'd office IP can 429 a colleague's login for up to a minute. That trade is
  // accepted for a rare, deliberate investigation surface — and is exactly why the
  // browse routes below are NOT here.
  '/api/admin/data-explorer/debug/*': { tier: 'STRICT', userLimit: 60, userWindow: 3600, failClosed: true },
  // Catch-all for the rest of the Data Explorer (`/search`, `/entity/*`, `/do/*`,
  // `/kv/*`, the DELETE routes). Added as a catch-all rather than another per-route
  // key because this budget kept lagging behind routes joining the debug boundary:
  // with no match the path falls to the default, which sets no `userLimit`, so the
  // per-user layer never runs at all. `getEndpointConfig` takes the longest match,
  // so `debug/*` above still wins.
  //
  // `/entity/*` deliberately has NO key of its own: it needs precisely this config,
  // and a duplicate entry is how the two drift apart on the next edit.
  //
  // `tier` is deliberately STANDARD, not STRICT. `tier` is the per-IP knob and
  // STRICT is 5/min keyed on IP ALONE — one bucket shared across every STRICT
  // endpoint. On these browse routes that breaks the console (open Browse KV and
  // inspect four values = 6 requests; expand six org rows in the search results =
  // 6 `/entity/org/:id` calls) and would drain the same IP's budget for
  // `/oauth/callback/*` and `/api/auth/magic-code/*`, which on a NAT'd office IP
  // means all admins. The per-user hourly cap is the leg that matters here.
  //
  // The canonical key is deliberate: raw pathnames contain DO ids and arbitrary
  // malformed requests can contain credential material. Keying on the raw pathname
  // both stores that material in Redis/logs and gives every target a fresh counter.
  // One stable identity makes every browse request consume the same per-admin
  // budget without storing request targets.
  //
  // 600/hour preserves the original pagination requirement (about 30k rows at
  // 50/page) while now genuinely bounding id/key enumeration across the surface.
  '/api/admin/data-explorer/*': {
    tier: 'STANDARD',
    userLimit: 600,
    userWindow: 3600,
    failClosed: true,
    userLimitKey: '/api/admin/data-explorer/*',
  },

  // Admin sandbox exec (harness-agents PR 1.5) — this is ARBITRARY CODE EXECUTION,
  // so it gets the strictest budget on the platform: STRICT tier + a low per-user
  // hourly cap (well below the 60/hr debug-read cap) + fail-closed. Platform-admin
  // is enforced at the route; this is the independent defense-in-depth signal so a
  // runaway exec loop surfaces as 429 instead of silently provisioning sandboxes.
  '/api/admin/sandbox/*': { tier: 'STRICT', userLimit: 20, userWindow: 3600, failClosed: true },

  // Admin harness incident-response (harness-agents PR 2.9b) — mass-destroy reaps
  // EVERY live harness sandbox/token/slot in scope, so it gets a strict budget
  // (STRICT + a low per-user hourly cap + fail-closed). Platform-admin is enforced
  // at the route; this is the independent defense-in-depth signal so a runaway
  // reap loop surfaces as 429. The break-glass disable/enable rides through
  // `/api/admin/config` (its own STANDARD budget), not this key.
  '/api/admin/harness/*': { tier: 'STRICT', userLimit: 10, userWindow: 3600, failClosed: true },

  // Platform-wide team-orphan repair — a single call can WRITE across every hub of
  // every org (HubDO purge + UserDO mirror fan-out), so it gets the same strict,
  // fail-closed budget as the other cross-platform admin write. Exact path (not a
  // prefix): the sibling `/api/admin/hubs/:hubId/harness` flag flips stay on the
  // default admin budget. Platform-admin is enforced at the route; this is the
  // defense-in-depth signal that a repair loop surfaces as 429.
  '/api/admin/hubs/repair-team-orphans': { tier: 'STRICT', userLimit: 10, userWindow: 3600, failClosed: true },

  // CI/GitOps endpoints - standard with user limits
  '/api/ci/*': { tier: 'STANDARD', userLimit: 100, skipAuthenticatedUsers: true },

  // Attested `/v1` proxy onto the Rekor Worker. `userLimitKey` is load-bearing here in a
  // way it is not for the siblings above: the per-user budget defaults to the full request
  // path, and this surface's paths carry base, record, and file ids — so without a pinned
  // key every distinct resource would get its own fresh budget and the per-user limit
  // would bound nothing. One shared budget across the whole data surface is the intent.
  [`${DATA_PROXY_PREFIX}/*`]: {
    tier: 'STANDARD',
    userLimit: 150,
    skipAuthenticatedUsers: true,
    userLimitKey: `${DATA_PROXY_PREFIX}/*`,
  },

  // App channel - high throughput with user limits
  '/channels/app/*': { tier: 'HIGH', userLimit: 500, skipAuthenticatedUsers: true },

  // API channel (api-channel-connector) - machine-to-machine, high throughput
  '/channels/api/*': { tier: 'HIGH', userLimit: 500, skipAuthenticatedUsers: true },

  // System/scheduled - high throughput (internal)
  '/channels/system/*': { tier: 'HIGH', skipAuthenticatedUsers: true },
  '/scheduled/*': { tier: 'HIGH', skipAuthenticatedUsers: true },

  // WebSocket - standard with user limit to prevent connection spam
  '/live-updates/ws': { tier: 'STANDARD', userLimit: 100, failClosed: true },

  // WS ticket mint - per-user 60/min accommodates aggressive reconnect patterns
  // without opening abuse vectors; fail-closed so Redis outages don't let
  // runaway clients mint unbounded tickets.
  '/api/auth/ws-ticket': { tier: 'MODERATE', userLimit: 60, failClosed: true, skipAuthenticatedUsers: true },
  '/api/auth/session-check': { tier: 'STANDARD', userLimit: 120, skipAuthenticatedUsers: true },
  '/api/auth/logout': { tier: 'MODERATE', userLimit: 10, failClosed: true, skipAuthenticatedUsers: true },
};

/**
 * Paths that should be excluded from rate limiting entirely
 *
 * Note: /files/* is the R2 file serving route (public/signed URLs, no auth)
 * This is different from /api/files/* which is the authenticated API route
 */
const EXCLUDED_PATHS = new Set(['/health', '/files/*']);

/**
 * Check if a path matches a pattern
 * Supports wildcards (*) at the end of patterns
 *
 * Examples:
 * - '/api/billing/*' matches '/api/billing', '/api/billing/', and '/api/billing/foo'
 * - '/api/billing/*' does NOT match '/api/billing-new'
 */
function matchPath(pattern: string, path: string): boolean {
  // Normalize: remove trailing slash for consistent matching
  const normalizedPath = path.replace(/\/$/, '');

  if (pattern.endsWith('/*')) {
    const prefix = pattern.slice(0, -2); // Remove '/*'
    return normalizedPath === prefix || normalizedPath.startsWith(prefix + '/');
  }
  return pattern === normalizedPath;
}

/**
 * Get rate limit configuration for a specific path
 * Returns null if the path is excluded from rate limiting
 */
export function getEndpointConfig(path: string): EndpointRateLimitConfig | null {
  // Check exclusions first
  for (const excluded of EXCLUDED_PATHS) {
    if (matchPath(excluded, path)) {
      return null;
    }
  }

  // Find matching config (most specific first - longer patterns)
  const sortedPatterns = Object.keys(ENDPOINT_CONFIGS).sort((a, b) => b.length - a.length);

  for (const pattern of sortedPatterns) {
    if (matchPath(pattern, path)) {
      return ENDPOINT_CONFIGS[pattern];
    }
  }

  // Default config for unlisted endpoints
  return { tier: 'STANDARD' };
}

/**
 * Get the rate limit tier configuration
 */
export function getTierConfig(tier: RateLimitTier): (typeof RATE_LIMIT_TIERS)[RateLimitTier] {
  return RATE_LIMIT_TIERS[tier];
}

/**
 * Distinct per-user rate-limit budgets whose window is 60s (`userWindow` unset or 60),
 * across every endpoint config. These are exactly the budgets that MUST have a native
 * `[[ratelimits]]` binding (see runtime `NATIVE_USER_LIMIT_BINDINGS`); the hourly
 * (`userWindow: 3600`) budgets are deliberately excluded — they stay Redis-only. Exported
 * so a CI test can assert the native binding map covers every 60s budget, surfacing drift
 * the moment a new 60s `userLimit` is added instead of failing silently in the shadow/
 * Phase-2 enforcement path.
 */
export function getSixtySecondUserLimits(): number[] {
  const limits = new Set<number>();
  for (const config of Object.values(ENDPOINT_CONFIGS)) {
    if (config.userLimit !== undefined && (config.userWindow ?? 60) === 60) {
      limits.add(config.userLimit);
    }
  }
  return [...limits].sort((a, b) => a - b);
}
